import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      // ─── Density + viewport-safe scrolling ─────────────────────────────────
      // `zoom: var(--app-shell-scale)` mirrors the app-shell scaling so
      // portaled modals stay visually consistent with the rest of the UI at
      // 100% browser zoom.
      // `maxHeight` compensates for the zoom so the modal visually fills 95vh
      // (without the /scale, `max-h-[95vh]` would shrink to ~66vh visually).
      style={{
        zoom: 'var(--app-shell-scale)',
        maxHeight: 'calc(95vh / var(--app-shell-scale, 0.7))',
        ...(props.style || {}),
      }}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background px-6 pb-6 pt-0 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
      {/* Note: the Close (✕) button now lives INSIDE DialogHeader (see below)
        * so it renders as an inline flex child alongside the title AND any
        * caller-provided action buttons (e.g. "Edit Task"). This eliminates
        * the overlap bug that occurred when the close was absolutely
        * positioned over the header. If a modal renders without a
        * <DialogHeader>, an accessible close is still injected here as a
        * fallback. */}
      <DialogPrimitive.Close
        className="dialog-close-fallback absolute right-5 top-5 z-20 p-1.5 rounded-md text-slate-500 opacity-80 ring-offset-background transition-all hover:opacity-100 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, children, ...props }) => (
  <div
    // ─── Flex-row header with proper spacing for title + actions + close ────
    // Layout:  [ Title / Description ]     [ user actions ]   [ ✕ ]
    // - `[&>*:first-child]:mr-auto` pushes the first child (typically the
    //   <DialogTitle> or a title wrapper) to grow and shove everything else
    //   to the right — so a caller-provided "Edit Task" button and the
    //   auto-injected close X naturally sit at the far right with a `gap-3`
    //   (12px) separation between them. No overlap, no clipping.
    // - `min-h-[68px]` guarantees breathing room; `justify-between` is
    //   avoided because it doesn't handle 3+ children as elegantly as
    //   `flex + mr-auto`.
    // - Marker class `dialog-header-present` lets the fallback close in
    //   DialogContent hide itself via CSS (`components.css` / global rule
    //   in index.css if needed) to avoid double-close-buttons.
    className={cn(
      "dialog-header-present sticky top-0 z-20 -mx-6 px-6 py-4 min-h-[68px] bg-background border-b border-slate-100 flex flex-row items-center gap-3 [&>*:first-child]:mr-auto",
      className
    )}
    {...props}>
    {children}
    <DialogPrimitive.Close asChild>
      <button
        type="button"
        aria-label="Close"
        className="shrink-0 p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors"
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </button>
    </DialogPrimitive.Close>
  </div>
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    // Sticky footer with matching visual weight against the header.
    className={cn(
      "sticky bottom-0 z-10 -mx-6 -mb-6 px-6 py-4 min-h-[68px] bg-background border-t border-slate-100 flex flex-col-reverse items-center sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
